import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Agence, InsertAgence } from "@shared/schema";
import { authFetch } from "@/lib/authFetch";

const AGENCES_QUERY_KEY = ["/api/agences"] as const;

async function readError(response: Response, fallback: string): Promise<never> {
  const body = await response.json().catch(() => ({}));
  throw new Error(body.message || fallback);
}

export function useAgences() {
  return useQuery<Agence[]>({
    queryKey: AGENCES_QUERY_KEY,
    queryFn: async () => {
      const response = await authFetch("/api/agences");
      if (!response.ok) return readError(response, "Impossible de charger les agences");
      return response.json();
    },
  });
}

export function useCreateAgence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: InsertAgence) => {
      const response = await authFetch("/api/agences", {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!response.ok) return readError(response, "Impossible de créer l'agence");
      return response.json() as Promise<Agence>;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: AGENCES_QUERY_KEY }),
  });
}

export function useUpdateAgence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & Partial<InsertAgence>) => {
      const response = await authFetch(`/api/agences/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
      if (!response.ok) return readError(response, "Impossible de modifier l'agence");
      return response.json() as Promise<Agence>;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: AGENCES_QUERY_KEY }),
  });
}

export function useDeleteAgence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await authFetch(`/api/agences/${id}`, { method: "DELETE" });
      if (!response.ok) return readError(response, "Impossible de supprimer l'agence");
      return response.json() as Promise<{ success: true }>;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: AGENCES_QUERY_KEY }),
  });
}